import { useState } from 'react';
import { D, st, toArr } from '../constants';
import { Overlay, MHdr, Field, StatGrid } from './shared';

const METHODS = ["Cash","Check","Credit Card","Zelle","Venmo","Other"];

export default function Deposits({deposits, clients, invoices, estimates, clientName, fmt, fmtDate, apiSaveDeposit, apiDeleteDeposit}) {
  const emptyForm = () => ({
    id: null,
    client_id: clients[0]?.id || '',
    linkedType: 'invoice',
    linkedId: '',
    amount: '',
    method: 'Cash',
    date: new Date().toISOString().slice(0,10),
    notes: '',
  });

  const [form, setForm] = useState(null);
  const [d, setD] = useState(emptyForm());
  const set = (k,v) => setD(x => ({...x, [k]: v}));

  const openAdd = () => { setD(emptyForm()); setForm('add'); };
  const openEdit = dep => {
    setD({
      id: dep.id,
      client_id: dep.client_id,
      linkedType: dep.invoice_id ? 'invoice' : dep.estimate_id ? 'estimate' : 'none',
      linkedId: dep.invoice_id || dep.estimate_id || '',
      amount: dep.amount,
      method: dep.method,
      date: dep.date,
      notes: dep.notes,
    });
    setForm('edit');
  };

  const save = async () => {
    const rec = {
      client_id: d.client_id || null,
      invoice_id: d.linkedType === 'invoice' ? (d.linkedId || null) : null,
      estimate_id: d.linkedType === 'estimate' ? (d.linkedId || null) : null,
      amount: parseFloat(d.amount) || 0,
      date: d.date,
      method: d.method,
      notes: d.notes,
    };
    await apiSaveDeposit(rec);
    setForm(null);
  };

  const remove = id => { if (confirm('Delete this deposit?')) apiDeleteDeposit(id); };

  const total = toArr(deposits).reduce((s,x) => s + (Number(x.amount)||0), 0);
  const byClient = {};
  toArr(deposits).forEach(x => { byClient[x.client_id] = (byClient[x.client_id]||0) + (Number(x.amount)||0); });

  const linkOptions = (cid, type) =>
    type === 'invoice' ? toArr(invoices).filter(i => i.clientId === cid) :
    type === 'estimate' ? toArr(estimates).filter(e => e.clientId === cid) : [];

  const linkedLabel = dep => {
    if (dep.invoice_id) {
      const inv = toArr(invoices).find(i => i.id === dep.invoice_id);
      return inv ? `Inv ${inv.number}` : `Invoice #${dep.invoice_id}`;
    }
    if (dep.estimate_id) {
      const est = toArr(estimates).find(e => e.id === dep.estimate_id);
      return est ? `Est ${est.number}` : `Estimate #${dep.estimate_id}`;
    }
    return '—';
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div style={st.h1}>Deposits</div>
        <button style={st.btn("primary")} onClick={openAdd}>+ Record Deposit</button>
      </div>

      <StatGrid stats={[
        { label: "Total deposits", value: fmt(total), color: D.success },
        { label: "Number of deposits", value: toArr(deposits).length },
        { label: "Clients with deposits", value: Object.keys(byClient).length, color: D.accent },
      ]}/>

      <div style={st.card}>
        {!toArr(deposits).length ? (
          <div style={{color:D.muted,textAlign:"center",padding:"24px 0"}}>No deposits recorded yet.</div>
        ) : (
          <div style={{overflowX:"auto"}}><table style={st.table}>
            <thead><tr>{["Date","Client","Linked To","Amount","Method","Notes",""].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
            <tbody>
              {toArr(deposits).slice().reverse().map(dep => (
                <tr key={dep.id}>
                  <td style={st.td}>{fmtDate(dep.date)}</td>
                  <td style={st.td}>{clientName(dep.client_id)}</td>
                  <td style={st.td}><span style={{color:D.accent,fontSize:12}}>{linkedLabel(dep)}</span></td>
                  <td style={{...st.td,color:D.success,fontWeight:500}}>{fmt(Number(dep.amount)||0)}</td>
                  <td style={st.td}>{dep.method}</td>
                  <td style={{...st.td,color:D.muted,fontSize:12}}>{dep.notes||"—"}</td>
                  <td style={st.td}>
                    <button style={st.btn()} onClick={()=>openEdit(dep)}>Edit</button>
                    <button style={st.btn("danger")} onClick={()=>remove(dep.id)}>Del</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      {form && (
        <Overlay>
          <div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(500px,95vw)"}}>
            <MHdr title={form==="add" ? "Record Deposit" : "Edit Deposit"} onClose={()=>setForm(null)}/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              <Field label="Date"><input style={st.input} type="date" value={d.date} onChange={e=>set("date",e.target.value)}/></Field>
              <Field label="Amount ($)"><input style={st.input} type="number" min="0" value={d.amount} onChange={e=>set("amount",e.target.value)} placeholder="0.00"/></Field>
              <div style={{gridColumn:"1/-1"}}>
                <Field label="Client">
                  <select style={st.input} value={d.client_id||""} onChange={e=>set("client_id",Number(e.target.value))}>
                    {toArr(clients).length===0 && <option value="">— No clients yet —</option>}
                    {toArr(clients).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Link to">
                <select style={st.input} value={d.linkedType} onChange={e=>{set("linkedType",e.target.value);set("linkedId","");}}>
                  <option value="invoice">Invoice</option>
                  <option value="estimate">Estimate</option>
                  <option value="none">None</option>
                </select>
              </Field>
              <Field label={d.linkedType==="invoice" ? "Invoice #" : d.linkedType==="estimate" ? "Estimate #" : "—"}>
                {d.linkedType!=="none" ? (
                  <select style={st.input} value={d.linkedId} onChange={e=>set("linkedId",Number(e.target.value))}>
                    <option value="">— Select —</option>
                    {linkOptions(d.client_id, d.linkedType).map(x=><option key={x.id} value={x.id}>{x.number}</option>)}
                  </select>
                ) : (
                  <input style={{...st.input,opacity:0.4}} disabled value="N/A"/>
                )}
              </Field>
              <Field label="Payment method">
                <select style={st.input} value={d.method} onChange={e=>set("method",e.target.value)}>
                  {METHODS.map(m=><option key={m}>{m}</option>)}
                </select>
              </Field>
              <Field label="Notes"><input style={st.input} value={d.notes||""} onChange={e=>set("notes",e.target.value)} placeholder="Optional"/></Field>
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",marginTop:20}}>
              <button style={st.btn()} onClick={()=>setForm(null)}>Cancel</button>
              <button style={st.btn("primary")} onClick={save}>Save</button>
            </div>
          </div>
        </Overlay>
      )}
    </div>
  );
}
